import { JSONContent } from '@tiptap/react';

import { FormTheme } from '@app/constants/theme';
import type { FieldRule } from '@app/lib/field-rules';
import { Parameters } from '@app/models/dtos/actions';
import { UserStatus } from '@app/models/dtos/user-status';
import { DateRule, FieldConditionalLogic, IFormFieldValidation, NodePosition, PageJump, RepeatSettings } from '@app/models/types/form-builder-shared';
import type { OptionFilter } from '@app/utils/option-logic';
import type { OptionSource } from '@app/utils/option-sources';

import { FormSlideLayout } from '../enums/form';
import { FileMetadata } from '../types/file-types';
import { ResponderGroupDto } from './groups';

/** One tab of a tabs container: its title and its questions (ids, in page order). A tab may be empty. */
export interface LayoutTab {
    id: string;
    title: string;
    fields?: string[];
}

/**
 * A layout container on a page (backend LayoutContainer): `panel` (a titled
 * group that opens and closes; `collapsed` = closed when the page opens),
 * `fieldset` (a bordered group with a legend) or `tabs` (a strip of `tabs`, no
 * title of its own). A panel and a field set list their questions in `fields`.
 * `logic` shows or hides the whole container. Layout only.
 */
/** Where a question was copied from: the workspace library's section, the version, the question's id in it and the insert it belongs to. */
export interface LibraryOrigin {
    sectionId: string;
    version: number;
    key: string;
    instance: string;
}

export interface LayoutContainer {
    id: string;
    type: 'panel' | 'fieldset' | 'tabs';
    title?: string;
    collapsed?: boolean;
    logic?: FieldConditionalLogic;
    fields?: string[];
    tabs?: LayoutTab[];
}

export interface StandardFormFieldProperties {
    hidden?: boolean;
    fields?: Array<StandardFormFieldDto>;
    placeholder?: string;
    choices?: Array<FieldChoice>;
    steps?: number;
    startFrom?: number;
    ratingShape?: string;
    dateFormat?: string;
    showQuestionNumbers?: boolean;
    allowMultipleSelection?: boolean;
    allowOtherChoice?: boolean;
    // 12-grid width of the field on desktop (12 = full row, 6 = half, 4 = a
    // third…). Adjacent fields whose spans fit pack into the same row; mobile
    // always stacks full-width. Default 12.
    colSpan?: number;
    layout?: FormSlideLayout;
    theme?: FormTheme;
    conditions?: any;
    mentions?: any;
    logicalOperator?: any;
    actions?: any;
    /** v2 conditional visibility rule for this field (see utils/conditional-logic). */
    logic?: FieldConditionalLogic;
    /** Field rules: require, lock, set a value or change the text of this question while a condition holds (see lib/field-rules.ts). */
    rules?: FieldRule[];
    /** A choice question's options filtered by an earlier choice question's answer (see utils/option-logic.ts). */
    optionFilter?: OptionFilter;
    /** The options come from a workspace list or another form's answers instead of `choices` (see utils/option-sources.ts). */
    optionSource?: OptionSource;
    /** A short text, dropdown or multiple choice question whose answers other forms of the workspace may show as choices (off unless true; see utils/option-sources.ts). */
    shareAsChoices?: boolean;
    /** Copied from a reusable section of the workspace's library (set by the server's insert, kept through saves). */
    library?: LibraryOrigin;
    /** v2 page-jump / branching rules for this slide (see utils/conditional-logic). */
    jumps?: PageJump[];
    /** Layout containers of this page: panels, field sets and tabs that group its questions (see utils/layout-containers.ts). */
    containers?: LayoutContainer[];
    /** Flow-view canvas position of this page (cosmetic; auto-layout when absent). */
    position?: NodePosition;
    rowTitles?: Array<string>;
    columnTitles?: Array<string>;
    tabular_value?: Array<Array<string>>;
    /** Present on a repeating group field (see utils/repeating-groups.ts). */
    repeat?: RepeatSettings;
    /** Short label shown with the control itself ("Start date" above a date picker), apart from the question title. */
    label?: string;
    /** Date, day and date range questions: constraints on the chosen date (each end of a range; see utils/date-rules.ts). */
    dateRules?: DateRule[];
    /** Time questions: the earliest and latest time that can be answered ("HH:MM", 24-hour; see utils/time-of-day.ts). */
    minTime?: string;
    maxTime?: string;
    /** Currency questions: the ISO 4217 codes respondents may pick, the first being the default (see utils/currency.ts). */
    currencies?: string[];
    /** Address questions: the countries a respondent may give (ISO 3166-1 alpha-2; absent means the Netherlands only; see utils/address.ts). */
    countries?: string[];
    /** Address questions: fill in street and city from a Dutch postcode and house number (absent means yes, while the server offers it). */
    addressLookup?: boolean;
    /** "Why we ask this": the creator's reason for an identifying question, shown to respondents under it (see utils/publish-checks.ts). */
    whyWeAsk?: string;
    /**
     * A question page's own section title (plain text, at most 120
     * characters): what the Leaflet section bar and the welcome page's
     * contents list show, before the page's opening heading and its first
     * question (utils/leaflet-sections.ts). '' clears it on a save; leaving
     * it out keeps the stored one.
     */
    sectionTitle?: string;
    /** A calculated field's formula as an expressions-v1 AST (utils/calculated-fields.ts); absent = no formula yet. */
    expression?: any;
    /** How a calculated field shows its value: number (decimals), currency, percent or text. */
    format?: { style?: 'number' | 'currency' | 'percent' | 'text'; decimals?: number; currency?: string };
    /** A calculated field respondents never see (computed and stored only). */
    hiddenFromRespondents?: boolean;
    /** A calculated field: unanswered questions it reads count as 0 (default: on unless the format is text). */
    unansweredAsZero?: boolean;
}

export interface StandardFormFieldDto {
    id: string;
    questionId?: string;
    formId?: string;
    validations?: IFormFieldValidation;
    properties?: StandardFormFieldProperties;
    value?: string;
    attachment?: any;
    title?: string | JSONContent;
    description?: string | null;
    type:
        | {
              type: string;
              options: Array<any>;
          }
        | any;
    isMediaContent?: boolean;
    mediaContent?: boolean;
    isGroupQuestion?: boolean;
    groupQuestion?: any;
    answer?: any;
    index: number;
    imageUrl?: string;
    /**
     * Internal ("for office use only") field: never shown to respondents;
     * workspace members fill it in on each submission (see utils/internal-fields).
     */
    internal?: boolean;
}

/** Who last changed one internal answer on a submission, and when. */
export interface InternalAnswerMeta {
    updated_by?: string;
    updated_by_email?: string;
    updated_at?: string;
}

/** One staff update on a submission as the respondent sees it (no staff identity). */
export interface RespondentFeedbackEntry {
    status?: string | null;
    message?: string | null;
    createdAt?: string;
}

/** The respondent's view of the feedback on their submission. */
export interface RespondentFeedbackView {
    /** The organisation (workspace title); never the staff member. */
    author?: string | null;
    currentStatus?: string | null;
    updatedAt?: string | null;
    /** Left out in listings, which only carry the current status. */
    entries?: RespondentFeedbackEntry[] | null;
}

/** One staff update as workspace members see it. */
export interface StaffFeedbackEntry extends RespondentFeedbackEntry {
    id?: string;
    createdBy?: string;
    createdByEmail?: string;
}

/** The feedback panel of a submission, for workspace members. */
export interface StaffFeedback {
    entries?: StaffFeedbackEntry[];
    currentStatus?: string | null;
    /** Workspace admins and the owner, while the form has feedback on. */
    canPost?: boolean;
    /** Whether a new update emails the respondent a notice. */
    notifiesRespondent?: boolean;
}

export interface StandardFormDto {
    formId: string;
    importedFormId?: string;
    title: string;
    description?: string | null | undefined;
    buttonText?: string;
    version?: number;
    provider?: string;
    responses?: number;
    deletionRequests?: number;
    groups?: Array<ResponderGroupDto>;
    builderVersion?: string;
    isMultiPage?: boolean;
    /**
     * Form-level access, while the server has it on: whether only selected people and teams can open this form in
     * the dashboard. The forms payload does not carry it yet (the Restricted chip on a card shows from it once it
     * does); a form's own page asks `GET .../forms/{id}/access` instead.
     */
    access?: { restricted: boolean; canManage?: boolean; permissions?: string[] };
    /**
     * Only on the published form read, while this form also sends its answers
     * to a case system: the organisation that runs it. The privacy panel names it.
     */
    registrationDestination?: { organisation: string } | null;
    /**
     * Only on the published form read, while an enabled webhook sends this form's
     * answers on: the host names of the destinations. The privacy panel names them.
     */
    webhookDestination?: { hosts: string[] } | null;
    /**
     * Only on the published form read, while an enabled email action mails this
     * form's answers on: `team` to the form's team, `copy` a copy to the
     * respondent. The privacy panel says so.
     */
    emailDestination?: { team: boolean; copy: boolean } | null;
    welcomePage?: {
        title?: string;
        description?: string;
        layout?: FormSlideLayout;
        imageUrl?: string;
        buttonText?: string;
    };
    thankyouPage?: Array<{
        /** Stable id that page jumps name (derived on read for older forms: utils/endings.ts). */
        id?: string;
        /** Short internal name ("Eligible"); respondents never see it. */
        name?: string;
        /** The ending a plain submit shows; when none is marked, the first. */
        isDefault?: boolean;
        title?: string;
        message?: string;
        buttonText?: string;
        buttonLink?: string;
        layout?: FormSlideLayout;
        imageUrl?: string;
    }>;
    settings?: {
        pinned: boolean;
        embedUrl?: string;
        customUrl: string;
        responseDataOwnerField?: string;
        private?: boolean;
        provider: string;
        isPublished?: boolean;
        roles?: Array<string>;
        privacyPolicyUrl?: string;
        /**
         * The language the form's own text is written in, e.g. "nl": one of
         * FORM_LANGUAGES (lib/form-ops/languages.ts), set in the builder's
         * Form tab or by the op set_form_language. Some imports set others
         * ("en-GB").
         */
        language?: string | null;
        /** An imported form that still needs a look (set by the import, cleared once its report is marked reviewed). */
        needsReview?: boolean | null;
        /** Where an imported form came from: "open_formulieren". */
        importSource?: string | null;
        /** The import record holding the report of an imported form. */
        importId?: string | null;
        /**
         * Where the form was last copied from (copying to another workspace): ids and a date, never a title. The server
         * sends it to people who may edit the form only.
         */
        promotedFrom?: { workspaceId: string; formId: string; at: string } | null;
        /** Trust-layer content: why the data is collected (shown to responders). */
        purpose?: string;
        /** Legacy free-text note on retention. Not shown to respondents: the period they read comes from responseExpiration* only (utils/retention.ts). */
        retentionText?: string;
        // "Allow AI insights on responses" (#716): while on, respondents see
        // a notice naming the provider; only responses submitted from a page
        // that showed it are analysed (aiNoticeToken, #752).
        aiInsightsEnabled?: boolean | null;
        aiInsightsProvider?: string | null;
        aiInsightsProviderName?: string | null;
        aiInsightsEnabledBy?: string | null;
        aiInsightsEnabledAt?: string | null;
        /** How long answers are kept: "days" (responseExpiration = a number of days), "date" (YYYY-MM-DD) or "forever". See utils/retention.ts. */
        responseExpiration?: string;
        responseExpirationType?: 'days' | 'date' | 'forever' | null;
        /** Set when the form was created under the publish checks (utils/publish-checks.ts); absent on older forms. */
        publishChecksVersion?: number | null;
        disableBranding: boolean;
        hidden: boolean;
        formCloseDate?: string;
        /** The instant the form starts taking responses; empty or absent = open from the start. */
        formOpenDate?: string;
        /** The number of responses after which the form takes no more. Only the form's team receives it (respondents learn that a form is full, never its limit). */
        responseLimit?: number | null;
        requireVerifiedIdentity?: boolean;
        showSubmissionNumber?: boolean;
        /** Respondents review their answers on a page of their own before sending (client-only; utils/review-page.ts). */
        showReviewPage?: boolean;
        allowEditingResponse?: boolean;
        /** "Let respondents save and continue later": drafts kept apart from the responses (off by default). */
        allowSaveDraft?: boolean | null;
        /** Days a draft is kept after its last save, 1 to 90; absent = 30 (utils/form-drafts.ts DEFAULT_DRAFT_DAYS). */
        draftRetentionDays?: number | null;
        showOriginalForm?: boolean;
        /** "Respond to submissions": staff post updates respondents see. */
        respondentFeedbackEnabled?: boolean;
        feedbackStatuses?: string[];
    };
    isPublished?: boolean;
    importerDetails?: UserStatus;
    fields: Array<StandardFormFieldDto>;
    /** Declared hidden-field (URL parameter) names — see utils/answer-piping.ts. */
    hiddenFields?: string[];
    createdTime?: string | Date;
    modifiedTime?: string | Date;
    coverImage?: string;
    logo?: string;
    /** Organisation logo settings (utils/form-logo.ts). */
    logoPosition?: 'left' | 'center' | 'auto';
    logoAlt?: string;
    useWorkspaceLogo?: boolean;
    createdAt?: string;
    updatedAt?: string;
    actions?: any;
    parameters?: Record<any, Array<Parameters>>;
    secrets?: Record<any, Array<Parameters>>;
    theme?: FormTheme;
    unauthorized?: boolean;
    /** Served without its questions, and why: "closed" once its close date has passed, "not_yet_open" before its open date, "full" once it holds as many responses as its limit. */
    unavailable?: UnavailableReason;
    /**
     * Signed proof that this page shows the AI insights notice (#752): sent
     * with the published form while it allows AI insights, and sent back on
     * submit. Only responses carrying a valid one are ever analysed.
     */
    aiNoticeToken?: string | null;
    /**
     * Signed proof of which questions' "may be shown as a choice" notice this
     * page shows: the backend hands it out with the published form while a
     * question shares its answers, and the page sends it back on submit
     * (`share_notice_token`). An answer is only ever offered to other forms
     * when it was submitted with a token naming its question.
     */
    shareNoticeToken?: string | null;
}

export type UnavailableReason = 'closed' | 'not_yet_open' | 'full';

export interface StandardFormResponseDto {
    answers: {
        [fieldId: string]: AnswerDto;
    };
    /** Captured hidden-field (URL parameter) values for this submission. */
    hiddenFields?: Record<string, string>;
    /** Staff-entered values of the form's internal fields (members only). */
    internalAnswers?: { [fieldId: string]: AnswerDto };
    internalAnswersMeta?: { [fieldId: string]: InternalAnswerMeta };
    /** Optimistic-concurrency counter for staff edits (0/absent = never edited). */
    internalAnswersVersion?: number;
    responseId: string;
    formId?: string;
    /** The published form version this response was submitted against. */
    formVersion?: number;
    formTitle?: string;
    formCustomUrl?: string;
    provider: string;
    createdAt: string | Date;
    updatedAt: string | Date;
    formImportedBy?: string;
    /** Set when the response was imported from a CSV file (the id of the import). */
    csvImportId?: string | null;
    /** Deletion request status (on deletion request lists): pending, success or declined. */
    status?: string;
    /** The deletion request's status on a single submission (GET /submissions/{id}): pending, success or declined. */
    deletionStatus?: string | null;
    /** Why a declined deletion request was declined (a fixed code, e.g. legal_obligation); on the lists and on a single submission. */
    declineReason?: string | null;
    /**
     * Parts of this response that were stored but could not be decrypted
     * ("answers", "hidden_fields", "internal_answers"). The server empties them
     * and names them here, so a blank is never mistaken for "no answer".
     */
    unreadableFields?: string[] | null;
    /** The receipt number responders use to find/delete this response. */
    submissionUuid?: string;
    /** Set (instead of dataOwnerIdentifier) when submitted anonymously. */
    anonymousIdentity?: string;
    /** On the receipt by number only (no sign-in, no identity fields): whether the response was submitted anonymously. */
    anonymize?: boolean;
    requestForDeletion?: boolean;
    expiration?: string;
    expirationType?: any;
    dataOwnerIdentifier?: string | null | undefined;
    responses?: Array<{ questionId: string; answer: any }>;
    /** Respondent-facing payloads only: the staff's updates on this submission. */
    feedback?: RespondentFeedbackView | null;
}

export interface WorkspaceResponderDto {
    email: string;
    responses: number;
    deletionRequests: number;
}

export interface AnswerDto {
    field: {
        id: string;
    };
    type: string;
    text?: string;
    number?: number;
    email?: string;
    boolean?: boolean;
    date?: string;
    time?: string;
    /** A currency question's answer: the ISO 4217 code and the amount as a plain decimal string ("12.50"). */
    currency?: { code?: string; amount?: string };
    /** A date range question's answer: its first and last day (YYYY-MM-DD). Stored responses may spell the slot `dateRange`. */
    date_range?: { start?: string; end?: string };
    dateRange?: { start?: string; end?: string };
    /** A tags question's answer: the labels, in the order they were entered. */
    tags?: string[];
    /** An address question's answer: its parts (see utils/address.ts). */
    address?: { street?: string; house_number?: string; house_number_addition?: string; postcode?: string; city?: string; country?: string };
    /**
     * A signature question's answer (utils/signature.ts): how it was given,
     * the typed name or the id of the drawn image (a private stored file),
     * when the server stored it, and the API path of the route that serves
     * the image (decrypted) which a single-response read adds for people who
     * may see it.
     */
    signature?: { method?: 'drawn' | 'typed' | string; file_id?: string; name?: string; signed_at?: string; url?: string };
    url?: string;
    file_metadata?: FileMetadata;
    phoneNumber?: string;
    phone_number?: string;
    tabular_value?: string[][];
    /** Repeating group answer: one answers map (child field id → answer) per item. */
    items?: Array<Record<string, any>>;
    choice?: {
        id?: string;
        /** For a question whose options come from a source: the text of the chosen option (the server writes it). */
        label?: string;
        value?: string;
        other?: string;
    };
    choices?: {
        /** Option id -> text, for a question whose options come from a source (the server writes it). */
        labels?: Record<string, string>;
        values?: Array<string>;
        other?: string;
    };
}

interface AttachmentProperties {
    description?: string;
}

enum AttachmentType {
    IMAGE = 'image',
    VIDEO = 'video'
}

enum EmbedProvider {
    YOUTUBE = 'youtube',
    VIEMO = 'vimeo',
    NO_EMBED = 'no_embed'
}

export interface FieldChoice {
    id: string;
    value?: string;
    label?: string;
    /** Choice-level logic: while it does not allow the option, respondents are not offered it (see utils/option-logic.ts). */
    logic?: FieldConditionalLogic;
}

export enum FieldTypes {
    TEXT = 'text',
    SLIDE = 'slide',
    YES_NO = 'yes_no',
    LINK = 'url',
    DATE = 'date',
    /** A date typed as day, month and year (answers like a date: `date`, YYYY-MM-DD). */
    DAY = 'day',
    /** A time of day, "HH:MM" (answer slot `time`). */
    TIME = 'time',
    /** An amount of money with its currency (answer slot `currency`). */
    CURRENCY = 'currency',
    /** A first and a last day (answer slot `date_range`). */
    DATE_RANGE = 'date_range',
    /** Free-entry short labels (answer slot `tags`). */
    TAGS = 'tags',
    /** A postal address: street, house number, postcode, city and country (answer slot `address`). */
    ADDRESS = 'address',
    /** A drawn or typed signature (answer slot `signature`). */
    SIGNATURE = 'signature',
    PHONE_NUMBER = 'phone_number',
    EMAIL = 'email',
    NUMBER = 'number',
    SHORT_TEXT = 'short_text',
    LONG_TEXT = 'long_text',
    MULTIPLE_CHOICE = 'multiple_choice',
    OPINION_SCALE = 'opinion_scale',
    RANKING = 'ranking',
    RATING = 'rating',
    LINEAR_RATING = 'linear_rating',
    DROP_DOWN = 'dropdown',
    MATRIX = 'matrix',
    TABULAR_INPUT = 'tabular_input',
    FILE_UPLOAD = 'file_upload',
    GROUP = 'group',
    /** A value computed from other answers (utils/calculated-fields.ts). */
    CALCULATED = 'calculated',
    PAYMENT = 'payment',
    STATEMENT = 'statement',
    VIDEO_CONTENT = 'VIDEO_CONTENT',
    IMAGE_CONTENT = 'IMAGE_CONTENT',
    DATE_INPUT = 'date_input',
    EMAIL_INPUT = 'email_input',
    NUMBER_INPUT = 'number_input',
    SHORT_TEXT_INPUT = 'short_text_input',
    LONG_TEXT_INPUT = 'long_text_input',
    MULTIPLE_CHOICE_INPUT = 'multiple_choice_input',
    RANKING_INPUT = 'ranking_input',
    RATING_INPUT = 'rating_input',
    DROP_DOWN_INPUT = 'drop_down_input',
    MEDIA_INPUT = 'media_input',
    MATRIX_ROW_INPUT = 'matrix_row_input',
    INPUT_FILE_UPLOAD = 'input_file_upload'
}

export const V2InputFields = [
    FieldTypes.DATE,
    FieldTypes.DAY,
    FieldTypes.TIME,
    FieldTypes.CURRENCY,
    FieldTypes.DATE_RANGE,
    FieldTypes.TAGS,
    FieldTypes.ADDRESS,
    FieldTypes.SIGNATURE,
    FieldTypes.YES_NO,
    FieldTypes.MULTIPLE_CHOICE,
    FieldTypes.RATING,
    FieldTypes.LINEAR_RATING,
    FieldTypes.NUMBER,
    FieldTypes.SHORT_TEXT,
    FieldTypes.EMAIL,
    FieldTypes.PHONE_NUMBER,
    FieldTypes.DROP_DOWN,
    FieldTypes.MATRIX,
    FieldTypes.TABULAR_INPUT
];
